import { useEffect, useMemo, useState } from "react";
import { T, fontCss } from "./theme";
import { loadConfig, saveConfig, hydrateConfig, type ProfileConfig } from "./api/config";
import { fetchEvents, fetchTasks, fetchEscalation, type SyncEvent } from "./api/cloud";
import { buildPayload } from "./lib/payload";
import { Today } from "./screens/Today";
import { Config } from "./screens/Config";
import { WakeCall } from "./screens/WakeCall";
import { DayPlan } from "./screens/DayPlan";
import { Checkin } from "./screens/Checkin";
import { Estimates } from "./screens/Estimates";
import { Escalation } from "./screens/Escalation";
import { Privacy } from "./screens/Privacy";

const SCREENS = [
  "today",
  "config",
  "call",
  "plan",
  "checkin",
  "estimates",
  "escalation",
  "privacy",
] as const;
type ScreenId = (typeof SCREENS)[number];
const LABELS: Record<ScreenId, string> = {
  today: "Today",
  config: "Config",
  call: "Wake call",
  plan: "Day plan",
  checkin: "Check-in",
  estimates: "Estimates",
  escalation: "Escalation",
  privacy: "Privacy",
};

export default function App() {
  const [active, setActive] = useState<ScreenId>("today");
  const [config, setConfigState] = useState<ProfileConfig>(() => loadConfig());
  const [events, setEvents] = useState<SyncEvent[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [escalation, setEscalation] = useState<any>(null);
  const [date, setDate] = useState<string>(() =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: config.timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date())
  );

  // Hydrate config from cloud on mount
  useEffect(() => {
    hydrateConfig().then((cloudConfig) => {
      if (cloudConfig) setConfigState(cloudConfig);
    });
  }, []);

  // Fetch events + tasks for the current date
  useEffect(() => {
    const tz = config.timezone;
    const today = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
    setDate(today);

    fetchEvents(today).then(setEvents);
    fetchTasks(today).then(setTasks);

    // Poll escalation every 5s when on the WakeCall screen
    const iv = setInterval(() => {
      fetchEscalation().then(setEscalation);
    }, 5000);
    fetchEscalation().then(setEscalation);
    return () => clearInterval(iv);
  }, [config.timezone]);

  const setConfig = (c: ProfileConfig) => {
    setConfigState(c);
    saveConfig(c);
  };

  // Build payload from REAL events (or fallback to empty if none)
  const payload = useMemo(() => {
    try {
      return buildPayload(config, events, date);
    } catch {
      return null;
    }
  }, [config, events, date]);

  return (
    <div
      className="zb"
      style={{
        minHeight: "100vh",
        background: `radial-gradient(120% 90% at 50% -10%, #241D3E 0%, ${T.night} 55%)`,
        padding: "28px 16px 40px",
      }}
    >
      <style>{fontCss}</style>

      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", color: T.gold, textTransform: "uppercase" }}>
            Always in Flux Industries
          </div>
          <div style={{ fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 750, color: T.paper, letterSpacing: "-0.02em", marginTop: 2 }}>
            Zoidberg <span style={{ color: T.dawn }}>Wake-Up</span>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap", marginBottom: 16 }}>
          {SCREENS.map((s) => (
            <button
              key={s}
              onClick={() => setActive(s)}
              style={{
                fontFamily: "'Spline Sans Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.06em",
                padding: "6px 10px",
                borderRadius: 999,
                cursor: "pointer",
                fontWeight: 600,
                background: active === s ? T.gold : "transparent",
                color: active === s ? T.night : T.mist,
                border: `1px solid ${active === s ? T.gold : T.edge}`,
                transition: "all .15s",
              }}
            >
              {LABELS[s]}
            </button>
          ))}
        </div>

        {/* Content - full width, no phone frame */}
        <div style={{ background: T.panel, border: `1px solid ${T.edge}`, borderRadius: 16, overflow: "hidden", minHeight: "50vh" }}>
          <Screen id={active} config={config} setConfig={setConfig} payload={payload} events={events} tasks={tasks} escalation={escalation} date={date} />
        </div>

        <div style={{ textAlign: "center", marginTop: 12, fontSize: 10, color: T.dim }}>
          Built for half-asleep eyes · no shame anywhere in the UI
        </div>
      </div>
    </div>
  );
}

function Screen({
  id,
  config,
  setConfig,
  payload,
  events,
  tasks,
  escalation,
  date,
}: {
  id: ScreenId;
  config: ProfileConfig;
  setConfig: (c: ProfileConfig) => void;
  payload: ReturnType<typeof buildPayload> | null;
  events: any[];
  tasks: any[];
  escalation: any;
  date: string;
}) {
  switch (id) {
    case "today":
      return payload ? (
        <Today payload={payload} tz={config.timezone} />
      ) : (
        <div style={{ padding: 24, color: T.dawn, fontSize: 13 }}>
          Can't compute the day — check the phone number in Config.
        </div>
      );
    case "config":
      return <Config config={config} setConfig={setConfig} />;
    case "call":
      return <WakeCall escalation={escalation} />;
    case "plan":
      return <DayPlan events={events} date={date} />;
    case "checkin":
      return <Checkin tasks={tasks} date={date} />;
    case "estimates":
      return <Estimates />;
    case "escalation":
      return <Escalation escalation={escalation} />;
    case "privacy":
      return <Privacy config={config} />;
  }
}
